import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import CampaignProgressSection from "./CampaignProgressSection";
import CampaignScheduledEmptyState from "./CampaignScheduledEmptyState";
import CampaignSortingEfficiencySection from "./CampaignSortingEfficiencySection";
import CampaignDistributionSection from "./CampaignDistributionSection";
import { useCampaignProgress } from "../hooks/useCampaignProgress";
import { useCampaignAnalysisData } from "../hooks/useCampaignAnalysisData";
import { getCampaignStatus } from "../hooks/useCampaignListData";
import Loading from "../../../components/utils/Loading";
import { useCampaignAnomalies } from "../hooks/useCampaignAnomalies";

const CampaignAnalysis = ({
  comments,
  campaignPeriods,
  selectedCampaign,
  isLoading,
  isLoadingCampaignPeriods,
}) => {
  const { t } = useTranslation();

  const campaignPeriod = useMemo(() => {
    if (
      !campaignPeriods ||
      selectedCampaign < 0 ||
      selectedCampaign >= campaignPeriods.length
    ) {
      return null;
    }
    return campaignPeriods[selectedCampaign];
  }, [campaignPeriods, selectedCampaign]);

  const campaignStatus = campaignPeriod
    ? getCampaignStatus(campaignPeriod)
    : null;
  const isScheduled = campaignStatus === "Scheduled";
  const showProgressSection =
    campaignStatus === "InProgress" || campaignStatus === "Completed";

  const isCampaignSelectionPending =
    campaignPeriods?.length > 0 &&
    (selectedCampaign < 0 || selectedCampaign >= campaignPeriods.length);

  const { monthlyTrackedObjects, isLoading: isLoadingAnomalies } =
    useCampaignAnomalies(isScheduled ? null : campaignPeriod);

  const { progress, isLoading: isLoadingProgress } = useCampaignProgress(
    showProgressSection ? campaignPeriod?._id : null,
  );

  const {
    processedData,
    retraitChartData,
    errorsByCategoryChartData,
    nonRetraitReasonsChartData,
    emplacementChartData,
  } = useCampaignAnalysisData(comments, t);

  if (isLoadingCampaignPeriods || isCampaignSelectionPending) {
    return <Loading color="#F5F5F5" />;
  }

  if (!campaignPeriod) {
    return (
      <div className="text-center text-[#5e5e5e] mt-8">
        {t("No campaign selected")}
      </div>
    );
  }

  if (isScheduled) {
    return <CampaignScheduledEmptyState campaignPeriod={campaignPeriod} />;
  }

  const chartsLoading = isLoading || isLoadingAnomalies;
  const accordionStoragePrefix = `campaign-${campaignPeriod._id}`;

  return (
    <div className="campaign-analysis-stack">
      {showProgressSection ? (
        <CampaignProgressSection
          progress={progress}
          isLoading={isLoadingProgress}
          campaignStop={campaignPeriod?.stop}
        />
      ) : null}

      <CampaignSortingEfficiencySection
        retraitChartData={retraitChartData}
        errorsByCategory={processedData.errorsByCategory}
        errorsByCategoryLabel={processedData.errorsByCategoryLabel}
        storageKey={`${accordionStoragePrefix}-sorting-efficiency`}
        isLoading={chartsLoading}
      />

      <CampaignDistributionSection
        comments={comments}
        campaignPeriod={campaignPeriod}
        monthlyTrackedObjects={monthlyTrackedObjects}
        errorsByCategoryChartData={errorsByCategoryChartData}
        nonRetraitReasonsChartData={nonRetraitReasonsChartData}
        emplacementChartData={emplacementChartData}
        storageKey={`${accordionStoragePrefix}-distribution`}
        isLoading={chartsLoading}
      />
    </div>
  );
};

export default CampaignAnalysis;
